import type { ReportOptions, SiteReportingService, UILabeledAggregation } from './types';
import {
  addDays,
  addHours,
  addMonths,
  addYears,
  differenceInDays,
  differenceInHours,
  differenceInMonths,
  differenceInYears,
} from 'date-fns';
import type { TimeDivision } from '../../../types';
import delay from '../mockServiceDelay';

const generateRandomOrderToDeliverBucket = (start: number, end: number, timeDivider: TimeDivision) => {
  const data: UILabeledAggregation[] = [];
  const labels = ['10', '20', '30', '40', '50', '60', '70', '80', '90'];
  let startMinValue = 1;
  // add values that always increase or stay the same but at a random rate for days in each percentile
  for (const label of labels) {
    const rando = Math.round(Math.random());
    const value = startMinValue + rando;
    if (startMinValue < 6) {
      startMinValue = value;
    }
    data.push({
      segmentStartTime: new Date(start).toISOString(),
      segmentEndTime: new Date(end).toISOString(),
      value,
      label,
      timeDivision: timeDivider,
      units: 'Days',
    });
  }
  return data;
};

const generateDeliveryPerformanceBucket = (start: number, end: number, timeDivider: TimeDivision) => {
  const data: UILabeledAggregation[] = [];
  const labels = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
  for (const label of labels) {
    const value = Math.floor(Math.random() * 10) + 1;
    data.push({
      segmentStartTime: new Date(start).toISOString(),
      segmentEndTime: new Date(end).toISOString(),
      value,
      label,
      timeDivision: timeDivider,
      units: 'Days',
    });
  }
  return data;
};

const generateHourlyReportData = (options: ReportOptions, bucketGenerator: any) => {
  const data: any[] = [];
  let start = new Date(options.startDate);
  let end = new Date(options.endDate);
  switch (options.timeDivider) {
    case 'Year':
      const years = differenceInYears(end, start);
      for (let i = 0; i <= years; i++) {
        const bucket = bucketGenerator(start.toISOString(), end.toISOString(), 'Year');
        start = addYears(start, 1);
        end = addYears(start, 1);
        data.push(...bucket);
      }
      return data;
    case 'Month':
      const months = differenceInMonths(end, start);
      for (let i = 0; i <= months; i++) {
        const bucket = bucketGenerator(start.toISOString(), end.toISOString(), 'Month');
        start = addMonths(start, 1);
        end = addMonths(start, 1);
        data.push(...bucket);
      }
      return data;
    case 'Week':
      const weeks = differenceInMonths(end, start);
      for (let i = 0; i <= weeks; i++) {
        const bucket = bucketGenerator(start.toISOString(), end.toISOString(), 'Week');
        start = addMonths(start, 1);
        end = addMonths(start, 1);
        data.push(...bucket);
      }
      return data;
    case 'Day':
      const days = differenceInDays(end, start);
      for (let i = 0; i <= days; i++) {
        const bucket = bucketGenerator(start.toISOString(), end.toISOString(), 'Day');
        start = addDays(start, 1);
        end = addDays(start, 1);
        data.push(...bucket);
      }
      return data;
    case 'Hour':
      const hours = differenceInHours(end, start);
      for (let i = 0; i <= hours; i++) {
        const bucket = bucketGenerator(start.toISOString(), end.toISOString(), 'Hour');
        start = addHours(start, 1);
        end = addHours(start, 1);
        data.push(...bucket);
      }
      return data;
  }
  return data;
};

export const mockSiteReportingService: SiteReportingService = {
  fetchAggregateReport: async (requestParams) => {
    await delay();
    let data = [];
    switch (requestParams.serviceLevelReportingType) {
      case 'stdDaysCount':
        data = generateHourlyReportData(requestParams, generateDeliveryPerformanceBucket);
        break;
      case 'otdDaysPercentile':
        data = generateHourlyReportData(requestParams, generateRandomOrderToDeliverBucket);
        break;
      case 'otsDaysCount':
        data = generateHourlyReportData(requestParams, generateDeliveryPerformanceBucket);
        break;
    }
    return data;
  },
  fetchCarrierCostingWeightReport: async (requestParams) => {
    await delay();
    return [];
  },
  createManifestHistoryReport: (requestParams) => {
    return Promise.resolve({
      manifestId: '11111',
      carrierResponseId: '22222',
      carrierResponseRaw: 'info_info_info_in_json_format',
    });
  },
};

export default mockSiteReportingService;
